import { useState, useEffect } from 'react';
import {
    Table,
    Typography,
} from "antd";
import CsvUpload from '../../imports/csvUpload';
import { errorMessage } from '../../../helpers/errorMessageFunc';
import GenericModal from '../../buttons/genericModal';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

const { Title } = Typography

const InitializePaymentsFromCSV = (props) => {
    const [csvData, setCsvData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const isInternalPayment = props.paymentType === 'internal';

    const columns = [
        {
            title: "fromLast4",
            dataIndex: "fromLast4",
        },
        {
            title: "toLast4",
            dataIndex: "toLast4",
        },
        {
            title: "description",
            dataIndex: "description",
        },
        ...(isInternalPayment ? [] : [{
            title: "addenda",
            dataIndex: "addenda",
        }]),
        ...(isInternalPayment ? [] : [{
            title: "method",
            dataIndex: "method",
        }]),
        {
            title: "amount",
            dataIndex: "amount",
        },
    ];

    const requiredFields = [
        "fromLast4",
        "toLast4",
        "description",
        ...(isInternalPayment ? [] : ["method"]),
        "amount",
    ];

    const preview = csvData.map((obj) => ({
        fromLast4: obj.fromLast4,
        toLast4: obj.toLast4,
        description: obj.description,
        ...(isInternalPayment ? {} : { addenda: obj.addenda }),
        ...(isInternalPayment ? {} : { method: obj.method }),
        amount: obj.amount,
    }));

    useEffect(() => {
        if (csvData && csvData.length > 0) {
            const firstRow = csvData[0];
            const anyKeyNeedsNormalization = Object.keys(firstRow).some(key => {
                const normalizedKey = key.replace(/["']/g, '').trim();
                return normalizedKey !== key;
            });

            if (anyKeyNeedsNormalization) {
                const normalizedData = csvData.map(row => {
                    const normalizedRow = {};
                    Object.keys(row).forEach(key => {
                        const normalizedKey = key.replace(/["']/g, '').trim();
                        normalizedRow[normalizedKey] = row[key];
                    });
                    return normalizedRow;
                });

                setCsvData(normalizedData);
            }
        }
    }, [csvData]);

    const handleSubmit = () => {
        if (csvData.length === 0) {
            return errorMessage(
                "Csv File provided is blank. Please upload a valid csv file."
            );
        };

        let sampleRowFromCsv = csvData[0];
        let fieldsFromCsv = Object.keys(sampleRowFromCsv);

        const isValidAmount = csvData.every(row => {
            let amount = row.amount?.replace(/\$|,/g, '');
            return !isNaN(parseFloat(amount));
        });

        const isValidFromLast4 = csvData.every(row => /^\d{4}$/.test(row.fromLast4));

        const allFieldsArePresent = requiredFields.every((field) => {
            return fieldsFromCsv.includes(field);
        });

        if (!allFieldsArePresent) {
            return errorMessage(
                "Csv File doesn't contain all the required fields. Please upload a valid csv file. Also check the spelling is same as shown in the bullet points", 10
            );
        }

        if (!isInternalPayment) {
            const methodValues = csvData.map(row => row.method.toUpperCase());
            const isValidAddenda = !csvData.some(row => row.method.toUpperCase() === 'WIRE' && (row.addenda !== undefined && row.addenda.length !== 0));
            const isValidMethod = methodValues.every(method => method === 'ACH' || method === 'WIRE');
            if (!isValidMethod) {
                return errorMessage(
                    "Csv File contains invalid values for 'method' field. Please upload a valid csv file. Only 'ACH' or 'Wire' are allowed", 10
                );
            }
            if (!isValidAddenda) {
                return errorMessage(
                    "'addenda' for 'WIRE' method will be ignored."
                );
            }
        }
        const isValidToLast4 = csvData.every(row => {
            return /^\d{4}$/.test(row.toLast4);
        });
        if (!isValidToLast4) {
            return errorMessage("Csv File contains invalid values for 'toLast4' field. Please upload a valid csv file. The 'toLast4' field should be last 4 digits of the receiving bank account number.");
        }

        if (!isValidAmount) {
            return errorMessage("Csv File contains invalid values for 'amount' field. Please upload a valid csv file. The 'amount' field should be a float.");
        }

        if (!isValidFromLast4) {
            return errorMessage("Csv File contains invalid values for 'fromLast4' field. Please upload a valid csv file. The 'fromLast4' field should be last 4 digits of the sending bank account number.");
        }

        props.onSuccess(csvData);
        setVisible(false);
        return;
    }

    return (
        <GenericModal
            size="lg"
            button={<Button disableElevation fullWidth size="small" type="submit" variant="outlined" color="secondary"
                sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}>Upload CSV</Button>}
            modalTitle={"Initialize payments from CSV file"}
            visible={visible}
            handleCancelModal={() => setVisible(false)}
            showModal={() => setVisible(true)}
            modalBody={
                <>
                    <div>
                        <Title level={5}>
                            Please make sure csv file contains all the below
                            mentioned fields!
                        </Title>

                        <ul
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                flexDirection: "column",
                                gap: "6px",
                            }}
                        >
                            {requiredFields.map((field) => {
                                return <li key={field}>{field}</li>;
                            })}
                        </ul>
                    </div>

                    <CsvUpload setCsvData={setCsvData} />

                    {csvData.length > 0 && (
                        <div style={{ margin: "10px 0" }}>
                            <Title level={5}>
                                Are you sure to continue with these payment instructions?{" "}
                            </Title>
                            <Table
                                pagination={{
                                    defaultPageSize: 5,
                                    showSizeChanger: true,
                                }}
                                columns={columns}
                                dataSource={preview}
                                tableLayout="fixed"
                                size="small"
                            />

                            <LoadingButton
                                disableElevation fullWidth size="large" type="submit" variant="contained" color="primary"
                                sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                                loading={loading}
                                loadingPosition='start'
                                onClick={handleSubmit}
                            >
                                Submit
                            </LoadingButton>
                        </div>
                    )}
                </>
            }
        ></GenericModal>
    );
}

export default InitializePaymentsFromCSV;